/**
 * useEmployeePayroll Composable
 *
 * Employee-centered payroll management composable.
 * Provides methods for managing employee payroll periods.
 *
 * Replaces the old period-centered approach with employee-first thinking.
 */

import { ref, computed } from 'vue';
import { httpService } from '../services/httpService';
import { useDateFormatter } from './useDateFormatter';
import { eventBus } from '../eventBus';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_CreatePeriodForEmployeeInput.ts';
import '../contracts/Payroll_EmployeeLastPeriodsBulkInput.ts';
import type { Payroll_EmployeesPayrollSummaryResponse } from '../contracts/Payroll_EmployeesPayrollSummaryResponse';
import '../contracts/Payroll_EmployeeLastPeriodResponse';
import '../contracts/Payroll_EmployeeLastPeriodsBulkResponse';
import '../contracts/Payroll_EmployeePayrollHistoryResponse';
import '../contracts/Payroll_EmployeesPayrollSummaryResponse';

import '../contracts/Payroll_AvailableCashRecebimentosResponse';
import '../contracts/Payroll_CreatePeriodForEmployeeResponse';
import '../contracts/Payroll_ExecutionResponse';
import '../contracts/Payroll_ExecutionsResponse';
import type { Payroll_CreatePeriodForEmployeeInput } from '../contracts/Payroll_CreatePeriodForEmployeeInput';
import type { Payroll_EmployeeLastPeriodsBulkInput } from '../contracts/Payroll_EmployeeLastPeriodsBulkInput';
export function useEmployeePayroll() {
  const isLoading = ref(false);
  const error = ref(null);

  // Employee payroll summary data
  const employeesSummary = ref([] as any);

  // Single employee payroll history
  const employeeHistory = ref([] as any);

  // Last period for auto-calculating start date
  const lastPeriod = ref(null as any);

  const { fromApiDateTimeFormat, toApiDateTimeFormat, formatDateTime, formatDate } = useDateFormatter();

  /**
   * Fetch all employees with their latest payroll info per franchise
   * Used by EmployeePayrollTable to display the main list
   */
  const listEmployeesPayrollSummary = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<Payroll_EmployeesPayrollSummaryResponse>(
        '/payroll/employees_payroll_summary',
        { responseContract: 'Payroll_EmployeesPayrollSummaryResponse' }
      );

      if (response.success) {
        employeesSummary.value = response.dados || [];

        // Convert ISO 8601 strings to Date objects for components
        employeesSummary.value = employeesSummary.value.map((employee: any) => ({
          ...employee,
          franchises: employee.franchises.map((franchise: any) => ({
            ...franchise,
            latest_period_end: franchise.latest_period_end
              ? fromApiDateTimeFormat(franchise.latest_period_end)
              : null
          }))
        }));

        return employeesSummary.value;
      } else {
        throw new Error(response.error || 'Failed to fetch employees summary');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar resumo de funcionários';
      console.error('[useEmployeePayroll] listEmployeesPayrollSummary error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch payroll history for a specific employee
   * Used by EmployeePayrollHistoryModal
   */
  const getEmployeePayrollHistory = async (userId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/employee_payroll_history/${userId}`, {
        responseContract: 'Payroll_EmployeePayrollHistoryResponse'
      });

      if (response.success) {
        const periods = response.dados?.periods || [];

        // Convert ISO 8601 strings to Date objects
        employeeHistory.value = periods.map((period: any) => ({
          ...period,
          period_start_date: fromApiDateTimeFormat(period.period_start_date),
          period_end_date: fromApiDateTimeFormat(period.period_end_date),
          created_at: fromApiDateTimeFormat(period.created_at),
          updated_at: period.updated_at ? fromApiDateTimeFormat(period.updated_at) : null
        }));

        return employeeHistory.value;
      } else {
        throw new Error(response.error || 'Failed to fetch employee history');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar histórico do funcionário';
      console.error('[useEmployeePayroll] getEmployeePayrollHistory error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch last period end dates for multiple user+franchise combinations
   * Used by CreatePeriodModal to display last period info in employee list
   *
   * @param {Array<{userId: string, franchiseId: number}>} combinations
   * @returns {Promise<Map<string, Date|null>>} Map keyed by "userId|franchiseId"
   */
  const getLastPeriodsBulk = async (combinations: any) => {
    if (!combinations || combinations.length === 0) {
      return new Map();
    }

    try {
      // Convert to API format
      const apiCombinations = combinations.map((c: any) => ({
        user_id: c.userId,
        franchise_id: c.franchiseId
      }));

      const payload: Payroll_EmployeeLastPeriodsBulkInput = { combinations: apiCombinations };
      validate('Payroll_EmployeeLastPeriodsBulkInput', payload);
      const response = await httpService.post('/payroll/employee_last_periods_bulk', payload, {
        responseContract: 'Payroll_EmployeeLastPeriodsBulkResponse'
      });

      if (response.success && response.dados) {
        const result = new Map();
        for (const [key, value] of Object.entries(response.dados)) {
          // Convert ISO 8601 string to Date object, or null if no period
          result.set(key, value ? fromApiDateTimeFormat(value as string) : null);
        }
        return result;
      } else {
        console.error('[useEmployeePayroll] getLastPeriodsBulk error:', response.error);
        return new Map();
      }
    } catch (err) {
      console.error('[useEmployeePayroll] getLastPeriodsBulk error:', err);
      return new Map();
    }
  };

  /**
   * Fetch the latest period for an employee+franchise
   * Used by CreatePeriodModal to auto-calculate start datetime
   */
  const getLastPeriodEnd = async (userId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/employee_last_period/${userId}/${franchiseId}`, {
        responseContract: 'Payroll_EmployeeLastPeriodResponse'
      });

      if (response.success && response.dados) {
        // Convert ISO 8601 string to Date object
        lastPeriod.value = {
          ...response.dados,
          period_start_date: fromApiDateTimeFormat(response.dados.period_start_date),
          period_end_date: fromApiDateTimeFormat(response.dados.period_end_date),
          created_at: fromApiDateTimeFormat(response.dados.created_at),
          updated_at: response.dados.updated_at ? fromApiDateTimeFormat(response.dados.updated_at) : null
        };

        // Return the end datetime for seamless boundary
        return lastPeriod.value?.period_end_date;
      } else {
        // No previous period found - this is the first period
        lastPeriod.value = null;
        return null;
      }
    } catch (err) {
      // If 404 or no data, this means first period
      if (err.response?.status === 404 || !err.response?.data?.dados) {
        lastPeriod.value = null;
        return null;
      }

      error.value = err.message || 'Erro ao buscar último período';
      console.error('[useEmployeePayroll] getLastPeriodEnd error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new payroll period for an employee
   * Used by CreatePeriodModal
   *
   * @param {string} userId - Employee's Cognito sub
   * @param {number} franchiseId - Franchise ID
   * @param {string} startDate - Period start date (YYYY-MM-DD format)
   * @param {string} endDate - Period end date (YYYY-MM-DD format)
   * @returns {Promise<object>} Created period data
   *
   * Note: Backend sets times automatically:
   * - Start: 00:00:00
   * - End: 23:59:59 if past day, current time if today
   */
  const createPeriodForEmployee = async (userId: any, franchiseId: any, startDate: any, endDate: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      // Send date-only strings (YYYY-MM-DD) - backend handles times
      const payload: Payroll_CreatePeriodForEmployeeInput = {
        user_id: userId,
        franchise_id: franchiseId,
        period_start_date: startDate,
        period_end_date: endDate
      };

      validate('Payroll_CreatePeriodForEmployeeInput', payload);
      const response = await httpService.post('/payroll/create_period_for_employee', payload, { responseContract: 'Payroll_CreatePeriodForEmployeeResponse' });

      if (response.success && response.dados) {
        const createdPeriod = response.dados;

        // Convert response dates back to Date objects
        const periodWithDates = {
          ...createdPeriod,
          period_start_date: fromApiDateTimeFormat(createdPeriod.period_start_date),
          period_end_date: fromApiDateTimeFormat(createdPeriod.period_end_date),
          created_at: fromApiDateTimeFormat(createdPeriod.created_at),
          updated_at: createdPeriod.updated_at ? fromApiDateTimeFormat(createdPeriod.updated_at) : null
        };

        // Emit event to refresh tables
        eventBus.emit('employeePayroll.periodCreated', {
          userId,
          franchiseId,
          periodId: createdPeriod.id
        });

        return periodWithDates;
      } else {
        throw new Error(response.error || 'Failed to create period');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao criar período';
      error.value = errorMessage;
      console.error('[useEmployeePayroll] createPeriodForEmployee error:', err);
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch a single execution by ID
   * Used by EmployeePaymentDetail
   */
  const getExecution = async (id: number | string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/execution/${id}`, {
        responseContract: 'Payroll_ExecutionResponse'
      });

      if (response.success) {
        return response.dados || null;
      } else {
        throw new Error(response.error || 'Failed to fetch execution');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar execução';
      console.error('[useEmployeePayroll] getExecution error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch executions for the current user
   * Used by EmployeePaymentsPage and EmployeePaymentsTable
   */
  const myExecutions = async (userId?: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/payroll/my-executions', {
        ...(userId ? { params: { user_id: userId } } : {}),
        responseContract: 'Payroll_ExecutionsResponse'
      });

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Failed to fetch my executions');
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar execuções';
      console.error('[useEmployeePayroll] myExecutions error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Computed: Get list of unique employees from summary
   */
  const uniqueEmployees = computed(() => {
    return employeesSummary.value.map((emp: any) => ({
      user_id: emp.user_id,
      user_name: emp.user_name,
      user_email: emp.user_email
    }));
  });

  /**
   * Computed: Get flat list of all employee+franchise combinations
   * Useful for AG Grid table display
   */
  const employeeFranchiseRows = computed(() => {
    const rows: any = [];

    employeesSummary.value.forEach((employee: any) => {
      employee.franchises.forEach((franchise: any) => {
        rows.push({
          user_id: employee.user_id,
          user_name: employee.user_name,
          user_email: employee.user_email,
          franchise_id: franchise.franchise_id,
          franchise_name: franchise.franchise_name,
          latest_period_end: franchise.latest_period_end,
          latest_period_status: franchise.latest_period_status,
          days_since_last_payroll: franchise.days_since_last_payroll,
          has_active_rules: franchise.has_active_rules
        });
      });
    });

    return rows;
  });

  /**
   * Helper: Format period datetime for display
   */
  const formatPeriodDateTime = (datetime: any) => {
    if (!datetime) return '-';
    return formatDateTime(datetime);
  };

  /**
   * Helper: Format period date (without time) for display
   */
  const formatPeriodDate = (datetime: any) => {
    if (!datetime) return '-';
    return formatDate(datetime);
  };

  /**
   * Helper: Get status label for period status
   */
  const getStatusLabel = (status: any) => {
    const statusMap: any = {
      0: 'Aberto',
      1: 'Receitas',
      2: 'Despesas',
      3: 'Calculado',
      4: 'Confirmado',
      5: 'Pago',
      '-1': 'Arquivado'
    };
    return statusMap[status] || 'Desconhecido';
  };

  /**
   * Helper: Get status color class
   */
  const getStatusColor = (status: any) => {
    const colorMap: any = {
      0: 'bg-blue-100 text-blue-800',
      1: 'bg-cyan-100 text-cyan-800',
      2: 'bg-purple-100 text-purple-800',
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-orange-100 text-orange-800',
      5: 'bg-green-100 text-green-800',
      '-1': 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  return {
    // State
    isLoading,
    error,
    employeesSummary,
    employeeHistory,
    lastPeriod,

    // Computed
    uniqueEmployees,
    employeeFranchiseRows,

    // Methods
    listEmployeesPayrollSummary,
    getEmployeePayrollHistory,
    getLastPeriodsBulk,
    getLastPeriodEnd,
    createPeriodForEmployee,
    getExecution,
    myExecutions,

    // Helpers
    formatPeriodDateTime,
    formatPeriodDate,
    getStatusLabel,
    getStatusColor
  };
}
